import { __extends } from "tslib";
import { EffectEnvMapMethod, _Shader_EffectEnvMapMethod } from './EffectEnvMapMethod';
/**
 * EffectFresnelEnvMapMethod provides a method to add fresnel-based reflectivity to an object using cube maps, which gets
 * stronger as the viewing angle becomes more grazing.
 */
var EffectFresnelEnvMapMethod = /** @class */ (function (_super) {
    __extends(EffectFresnelEnvMapMethod, _super);
    /**
     * Creates a new <code>EffectFresnelEnvMapMethod</code> object.
     *
     * @param envMap The environment map containing the reflected scene.
     * @param alpha The reflectivity of the material.
     */
    function EffectFresnelEnvMapMethod(envMap, alpha, fresnelPower, normalReflectance) {
        if (alpha === void 0) { alpha = 1; }
        if (fresnelPower === void 0) { fresnelPower = 5; }
        if (normalReflectance === void 0) { normalReflectance = 0; }
        var _this = _super.call(this, envMap, alpha) || this;
        _this._fresnelPower = fresnelPower;
        _this._normalReflectance = normalReflectance;
        return _this;
    }
    Object.defineProperty(EffectFresnelEnvMapMethod.prototype, "assetType", {
        /**
         * @inheritDoc
         */
        get: function () {
            return EffectFresnelEnvMapMethod.assetType;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(EffectFresnelEnvMapMethod.prototype, "fresnelPower", {
        /**
         * The power used in the Fresnel equation. Higher values make the fresnel effect more pronounced. Defaults to 5.
         */
        get: function () {
            return this._fresnelPower;
        },
        set: function (value) {
            this._fresnelPower = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(EffectFresnelEnvMapMethod.prototype, "normalReflectance", {
        /**
         * The minimum amount of reflectance, ie the reflectance when the view direction is normal to the surface or light direction.
         */
        get: function () {
            return this._normalReflectance;
        },
        set: function (value) {
            if (this._normalReflectance == value)
                return;
            this._normalReflectance = value;
            this.invalidate();
        },
        enumerable: true,
        configurable: true
    });
    EffectFresnelEnvMapMethod.assetType = '[asset EffectFresnelEnvMapMethod]';
    return EffectFresnelEnvMapMethod;
}(EffectEnvMapMethod));
export { EffectFresnelEnvMapMethod };
import { ShaderBase } from '@awayjs/renderer';
/**
 * _Shader_EffectFresnelEnvMapMethod provides a method to add fresnel-based reflectivity to an object using cube maps, which gets
 * stronger as the viewing angle becomes more grazing.
 */
var _Shader_EffectFresnelEnvMapMethod = /** @class */ (function (_super) {
    __extends(_Shader_EffectFresnelEnvMapMethod, _super);
    function _Shader_EffectFresnelEnvMapMethod() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    /**
     * @inheritDoc
     */
    _Shader_EffectFresnelEnvMapMethod.prototype._getFragmentCode = function (targetReg, registerCache, sharedRegisters) {
        var dataRegister = registerCache.getFreeFragmentConstant();
        var code = '';
        var viewDirReg = sharedRegisters.viewDirFragment;
        var normalReg = sharedRegisters.normalFragment;
        this._fragmentIndex = dataRegister.index * 4;
        var temp = registerCache.getFreeFragmentVectorTemp();
        registerCache.addFragmentTempUsages(temp, 1);
        var temp2 = registerCache.getFreeFragmentVectorTemp();
        registerCache.addFragmentTempUsages(temp2, 1);
        // r = V - 2(V.N)*N
        code += 'dp3 ' + temp + '.w, ' + viewDirReg + '.xyz, ' + normalReg + '.xyz\n' +
            'add ' + temp + '.w, ' + temp + '.w, ' + temp + '.w\n' +
            'mul ' + temp + '.xyz, ' + normalReg + '.xyz, ' + temp + '.w\n' +
            'sub ' + temp + '.xyz, ' + temp + '.xyz, ' + viewDirReg + '.xyz\n' +
            this._envMap._getFragmentCode(temp, registerCache, sharedRegisters, temp) +
            'sub ' + temp2 + '.w, ' + temp + '.w, fc0.x\n' + // -.5
            'kil ' + temp2 + '.w\n' + // used for real time reflection mapping - if alpha is not 1 (mock texture) kil output
            'sub ' + temp + ', ' + temp + ', ' + targetReg + '\n';
        // calculate fresnel term
        code += 'dp3 ' + viewDirReg + '.w, ' + viewDirReg + '.xyz, ' + normalReg + '.xyz\n' + // dot(V, H)
            'sub ' + viewDirReg + '.w, ' + dataRegister + '.w, ' + viewDirReg + '.w\n' + // base = 1-dot(V, H)
            'pow ' + viewDirReg + '.w, ' + viewDirReg + '.w, ' + dataRegister + '.z\n' + // exp = pow(base, 5)
            'sub ' + normalReg + '.w, ' + dataRegister + '.w, ' + viewDirReg + '.w\n' + // 1 - exp
            'mul ' + normalReg + '.w, ' + dataRegister + '.y, ' + normalReg + '.w\n' + // f0*(1 - exp)
            'add ' + viewDirReg + '.w, ' + viewDirReg + '.w, ' + normalReg + '.w\n' + // exp + f0*(1 - exp)
            // total alpha
            'mul ' + viewDirReg + '.w, ' + dataRegister + '.x, ' + viewDirReg + '.w\n';
        if (this._maskMap) {
            code += this._maskMap._getFragmentCode(temp2, registerCache, sharedRegisters, sharedRegisters.uvVarying) +
                'mul ' + viewDirReg + '.w, ' + temp2 + '.x, ' + viewDirReg + '.w\n';
        }
        // blend
        code += 'mul ' + temp + ', ' + temp + ', ' + viewDirReg + '.w\n' +
            'add ' + targetReg + ', ' + targetReg + ', ' + temp + '\n';
        registerCache.removeFragmentTempUsage(temp);
        registerCache.removeFragmentTempUsage(temp2);
        return code;
    };
    _Shader_EffectFresnelEnvMapMethod.prototype._updateProperties = function () {
        var index = this._fragmentIndex;
        var data = this._shader.fragmentConstantData;
        data[index] = this._method.alpha;
        data[index + 1] = this._method.normalReflectance;
        data[index + 2] = this._method.fresnelPower;
    };
    return _Shader_EffectFresnelEnvMapMethod;
}(_Shader_EffectEnvMapMethod));
export { _Shader_EffectFresnelEnvMapMethod };
ShaderBase.registerAbstraction(_Shader_EffectFresnelEnvMapMethod, EffectFresnelEnvMapMethod);
